import type { TFunction } from "i18next";
import {
  formatDayMonthSmartYear,
  formatNumber,
  formatVolume,
} from "@/lib/format";
import {
  describeOrderChangedDetail,
  describeVehicleChangedDetail,
} from "@/lib/buildChanges";
import {
  describeIneligiblePickDetail,
  isIneligiblePickReason,
  readPickRoute,
} from "@/lib/ineligiblePicks";
import {
  isKnownUnassignedReason,
  type UnassignedBreakdownPayload,
  type UnassignedNotificationOrder,
} from "@/types/routes";
import type { TenantPlanningSettings } from "@/types/settings";

export type {
  UnassignedBreakdownPayload,
  UnassignedNotificationOrder,
} from "@/types/routes";

/**
 * Parsing + grouping for the route-build unassigned breakdown (TASK-4740 payload).
 *
 * This module is the ONE place the raw solver reason code is turned into
 * user-visible text. The bell row, the build toasts and the routes-list alert all
 * route through it, so a dispatcher cannot see the reason translated one way in
 * the toast and another way in the bell — and there is exactly one place where a
 * NEW solver code has to be handled.
 */

/** One reason and every itemized order the solver dropped for it. */
export interface UnassignedReasonGroup {
  /** The raw solver code, e.g. `capacity` — verbatim from the backend. */
  reason: string;
  /**
   * False when the code has no `routes.buildAlert.reasons.*` entry, i.e. the
   * solver emitted a code newer than this bundle. Drives the readable fallback.
   */
  known: boolean;
  /** Translated reason heading — never a raw key path, never blank. */
  label: string;
  /** Actionable advice; `undefined` for an unknown code (we have no advice). */
  suggestion: string | undefined;
  /** Display labels for the itemized orders in this group, in payload order. */
  orderLabels: string[];
  /**
   * The worded measured cause per itemized order (same order as `orders`), or
   * `null` where the backend sent no recognizable detail — see
   * {@link describeUnassignedDetail}.
   */
  orderDetails: (string | null)[];
  /** The itemized orders themselves, for callers needing ids/addresses. */
  orders: UnassignedNotificationOrder[];
  /**
   * Total dropped for this reason across the WHOLE build, taken from
   * `unassigned_reason_counts` — so it stays truthful past the item cap.
   */
  total: number;
  /** `total` minus the itemized orders: the "and N more" count for this group. */
  hiddenCount: number;
}

/** The whole breakdown, ready to render. */
export interface UnassignedBreakdown {
  groups: UnassignedReasonGroup[];
  /** Total dropped orders across every reason. */
  total: number;
  /** True when the itemized list does not name every dropped order. */
  truncated: boolean;
  /** Dropped orders not named anywhere in `groups`. */
  hiddenCount: number;
}

/**
 * Display label for one dropped order: title → address → `#id`.
 *
 * Never returns an empty string, so a list row is never a blank line even when
 * the backend enrichment found neither a title nor an address.
 */
export function unassignedOrderLabel(
  order: UnassignedNotificationOrder,
): string {
  const title = order.order_title?.trim();
  if (title) return title;
  const address = order.order_address?.trim();
  if (address) return address;
  return `#${order.order_id}`;
}

/**
 * Translates a raw solver reason code into a heading.
 *
 * A code with no locale entry falls back to
 * `routes.buildAlert.reasons.unknown` — which interpolates the raw code, so the
 * dispatcher still learns something specific and support can grep for it. It is
 * never the bare key path and never blank; the backend used to collapse an
 * unmapped code to `""`, which is precisely the defect TASK-4740 removed and
 * that must not reappear on this side of the wire.
 */
export function translateUnassignedReason(
  reason: string,
  t: TFunction,
): string {
  if (isKnownUnassignedReason(reason)) {
    return t(`routes.buildAlert.reasons.${reason}`);
  }
  return t("routes.buildAlert.reasons.unknown", { code: reason });
}

/**
 * Narrows `extra_data` to the unassigned breakdown, or `null` when the payload
 * carries none.
 *
 * Defensive on purpose: `extra_data` is an untyped `JSONField`, older
 * notification rows predate TASK-4740 entirely, and a bell panel must not blow
 * up on a row written by a previous backend release.
 */
export function parseUnassignedPayload(
  extraData: Record<string, unknown> | null | undefined,
): UnassignedBreakdownPayload | null {
  if (!extraData || typeof extraData !== "object") return null;

  const rawItems = extraData.unassigned;
  const rawCounts = extraData.unassigned_reason_counts;
  const hasItems = Array.isArray(rawItems);
  const hasCounts =
    typeof rawCounts === "object" &&
    rawCounts !== null &&
    !Array.isArray(rawCounts);
  if (!hasItems && !hasCounts) return null;

  const items: UnassignedNotificationOrder[] = (
    hasItems ? (rawItems as unknown[]) : []
  ).flatMap((entry) => {
    if (typeof entry !== "object" || entry === null) return [];
    const record = entry as Record<string, unknown>;
    const orderId = Number(record.order_id);
    if (!Number.isFinite(orderId)) return [];
    return [
      {
        order_id: orderId,
        reason: typeof record.reason === "string" ? record.reason : "",
        order_title:
          typeof record.order_title === "string" ? record.order_title : null,
        order_address:
          typeof record.order_address === "string"
            ? record.order_address
            : null,
        detail:
          typeof record.detail === "object" &&
          record.detail !== null &&
          !Array.isArray(record.detail)
            ? (record.detail as Record<string, unknown>)
            : null,
        // BUG-685: the deleted copy of a permanent order.
        ...(record.from_permanent === true
          ? {
              from_permanent: true,
              template_id: Number.isFinite(Number(record.template_id))
                ? Number(record.template_id)
                : null,
            }
          : {}),
      },
    ];
  });

  const counts: Record<string, number> = {};
  if (hasCounts) {
    for (const [key, value] of Object.entries(
      rawCounts as Record<string, unknown>,
    )) {
      const count = Number(value);
      if (Number.isFinite(count) && count > 0) counts[key] = count;
    }
  }

  if (items.length === 0 && Object.keys(counts).length === 0) return null;

  const countsTotal = Object.values(counts).reduce((sum, n) => sum + n, 0);
  const rawTotal = Number(extraData.unassigned_total);
  const total = Number.isFinite(rawTotal)
    ? rawTotal
    : Math.max(countsTotal, items.length);

  return {
    unassigned_total: total,
    unassigned_truncated:
      extraData.unassigned_truncated === true || total > items.length,
    unassigned_reason_counts: counts,
    unassigned: items,
  };
}

/**
 * The route limits a tenant's planning settings ACTUALLY configure (BUG-685).
 * Only a positive value is a limit — `null` (and a meaningless 0) is «no limit».
 */
export interface ConfiguredRouteLimits {
  maxDistanceKm?: number;
  maxDurationMin?: number;
  maxOrdersPerRoute?: number;
  maxIdleTimeMin?: number;
}

const positive = (value: number | null | undefined): number | undefined =>
  typeof value === "number" && Number.isFinite(value) && value > 0
    ? value
    : undefined;

/**
 * Reads {@link ConfiguredRouteLimits} from the planning settings, or `null` while
 * they are not loaded — «unknown», which is NOT the same as «none configured».
 */
export function routeLimitsFromSettings(
  settings:
    | Pick<
        TenantPlanningSettings,
        | "max_distance_km"
        | "max_duration_min"
        | "max_orders_per_route"
        | "max_idle_time_min"
      >
    | null
    | undefined,
): ConfiguredRouteLimits | null {
  if (!settings) return null;
  const limits: ConfiguredRouteLimits = {};
  const distance = positive(settings.max_distance_km);
  const duration = positive(settings.max_duration_min);
  const orders = positive(settings.max_orders_per_route);
  const idle = positive(settings.max_idle_time_min);
  if (distance !== undefined) limits.maxDistanceKm = distance;
  if (duration !== undefined) limits.maxDurationMin = duration;
  if (orders !== undefined) limits.maxOrdersPerRoute = orders;
  if (idle !== undefined) limits.maxIdleTimeMin = idle;
  return limits;
}

/** True when at least one route limit is configured. */
export function hasConfiguredRouteLimits(
  limits: ConfiguredRouteLimits | null | undefined,
): boolean {
  return !!limits && Object.keys(limits).length > 0;
}

/**
 * The advice under «Не удалось определить причину» (BUG-685).
 *
 * It used to send every tenant to «лимиты маршрута в настройках планирования и
 * смены ТС» — including a tenant with no limits at all, who then had nothing to
 * check. Now it names only the limits that are actually configured, with their
 * values; with none (or while the settings are still loading) it says honestly
 * that the cause is unknown and what to do next.
 */
export function describeUndeterminedAdvice(
  limits: ConfiguredRouteLimits | null | undefined,
  t: TFunction,
): string {
  if (!limits || !hasConfiguredRouteLimits(limits)) {
    return t("routes.buildAlert.suggestions.undetermined");
  }
  const parts: string[] = [];
  if (limits.maxDistanceKm !== undefined) {
    parts.push(
      t("routes.buildAlert.undeterminedLimits.maxDistance", {
        value: whole(limits.maxDistanceKm),
      }),
    );
  }
  if (limits.maxDurationMin !== undefined) {
    parts.push(
      t("routes.buildAlert.undeterminedLimits.maxDuration", {
        value: whole(limits.maxDurationMin),
      }),
    );
  }
  if (limits.maxOrdersPerRoute !== undefined) {
    parts.push(
      t("routes.buildAlert.undeterminedLimits.maxOrders", {
        count: Math.round(limits.maxOrdersPerRoute),
        value: whole(limits.maxOrdersPerRoute),
      }),
    );
  }
  if (limits.maxIdleTimeMin !== undefined) {
    parts.push(
      t("routes.buildAlert.undeterminedLimits.maxIdle", {
        value: whole(limits.maxIdleTimeMin),
      }),
    );
  }
  return t("routes.buildAlert.undeterminedLimits.advice", {
    limits: parts.join(", "),
  });
}

/** Context that changes how a breakdown is worded. */
export interface UnassignedBreakdownOptions {
  /**
   * The tenant's configured route limits, or `null`/absent while unknown — the
   * `undetermined` advice then falls back to its neutral wording.
   */
  routeLimits?: ConfiguredRouteLimits | null;
  /**
   * BUG-694: the build's route date (`YYYY-MM-DD`) when it is ONE date — a
   * next-night window then says «ночь на 23 окт.». Absent (unknown, or several
   * dates) → «ночь после даты маршрута».
   */
  routeDate?: string | null;
}

/** True when the dropped order was a (now deleted) copy of a permanent order. */
export function isDeletedPermanentCopy(
  order: Pick<UnassignedNotificationOrder, "from_permanent">,
): boolean {
  return order.from_permanent === true;
}

/**
 * Groups a payload by reason and translates each heading.
 *
 * Group ORDER is the per-reason total, descending — the biggest cause of the
 * failure reads first, which is the question the dispatcher actually asked.
 * Reasons present only in `unassigned_reason_counts` (every one of their orders
 * fell past the item cap) still get a group, carrying a count with no names,
 * rather than vanishing from the breakdown entirely.
 */
export function buildUnassignedBreakdown(
  payload: UnassignedBreakdownPayload,
  t: TFunction,
  options: UnassignedBreakdownOptions = {},
): UnassignedBreakdown {
  const byReason = new Map<string, UnassignedNotificationOrder[]>();
  for (const order of payload.unassigned) {
    const existing = byReason.get(order.reason);
    if (existing) existing.push(order);
    else byReason.set(order.reason, [order]);
  }

  // Every reason we know about: itemized ones plus count-only ones.
  const reasons = new Set<string>([
    ...byReason.keys(),
    ...Object.keys(payload.unassigned_reason_counts),
  ]);

  const groups: UnassignedReasonGroup[] = [...reasons].map((reason) => {
    const orders = byReason.get(reason) ?? [];
    const total = payload.unassigned_reason_counts[reason] ?? orders.length;
    const known = isKnownUnassignedReason(reason);
    return {
      reason,
      known,
      label: translateUnassignedReason(reason, t),
      suggestion: !known
        ? undefined
        : reason === "undetermined"
          ? describeUndeterminedAdvice(options.routeLimits, t)
          : t(`routes.buildAlert.suggestions.${reason}`),
      orderLabels: orders.map(unassignedOrderLabel),
      orderDetails: orders.map((order) =>
        describeUnassignedDetail(reason, order.detail, t, {
          routeDate: options.routeDate,
        }),
      ),
      orders,
      total,
      hiddenCount: Math.max(0, total - orders.length),
    };
  });

  groups.sort((a, b) => b.total - a.total || a.reason.localeCompare(b.reason));

  const namedCount = groups.reduce((sum, g) => sum + g.orders.length, 0);
  const total = Math.max(payload.unassigned_total, namedCount);

  return {
    groups,
    total,
    truncated: payload.unassigned_truncated || total > namedCount,
    hiddenCount: Math.max(0, total - namedCount),
  };
}

/**
 * Flattens a breakdown into one plain-text paragraph for a toast description.
 *
 * A toast has no room for a list, so each reason becomes
 * `<reason>: <order>, <order> (+N more)` and the reasons are joined by `; `.
 * Kept text-only because `toastError`/`toastWarning` take a `description`
 * STRING (TASK-4739) — this is the same data as the bell's expandable rows, just
 * linearized, so the two surfaces cannot drift apart.
 */
export function formatUnassignedBreakdownText(
  breakdown: UnassignedBreakdown,
  t: TFunction,
): string {
  return breakdown.groups
    .map((group) => {
      const names = [...group.orderLabels];
      if (group.hiddenCount > 0) {
        names.push(
          t("routes.buildAlert.showMore", { count: group.hiddenCount }),
        );
      }
      return names.length > 0
        ? `${group.label}: ${names.join(", ")}`
        : `${group.label} (${group.total})`;
    })
    .join("; ");
}

/** How many distinct addresses a reason lists before collapsing into «и ещё N». */
export const MAX_ADDRESSES_PER_REASON = 5;

/** One place a reason dropped orders at, with how many orders it dropped there. */
export interface UnassignedAddressLine {
  /** The address as first written in the payload (title / `#id` when it has none). */
  label: string;
  /** Orders of this reason at this address. */
  count: number;
  /** Measured figures behind the reason, already worded; `null` when none were sent. */
  detail: string | null;
  /**
   * BUG-685: at least one order on this line was this date's copy of a permanent
   * order, deleted because it reached no route — the line says so once.
   */
  fromPermanent: boolean;
}

/** One reason, its orders collapsed by address (BUG-676). */
export interface UnassignedReasonSummary {
  reason: string;
  /** Translated reason heading — see {@link translateUnassignedReason}. */
  label: string;
  /** At most `maxLines` addresses, the most-affected first. */
  lines: UnassignedAddressLine[];
  /** Orders of this reason across the whole build. */
  total: number;
  /**
   * Orders of this reason NOT covered by `lines` — the overflow addresses plus any
   * orders the backend did not itemize — i.e. the «и ещё N» count.
   */
  hiddenCount: number;
}

/**
 * Grouping key for an address. Operators type the same point inconsistently —
 * «г. Толочин» and «г.Толочин», a stray trailing comma — and BUG-676 showed one
 * shop three times in a row. Case, whitespace and `.,;` runs are not meaningful.
 */
function addressKey(text: string): string {
  return text
    .toLocaleLowerCase()
    .replace(/[\s.,;]+/g, " ")
    .trim();
}

const finiteNumber = (value: unknown): number | null => {
  const n =
    typeof value === "string" && value.trim() !== "" ? Number(value) : value;
  return typeof n === "number" && Number.isFinite(n) ? n : null;
};

const nonEmptyString = (value: unknown): string | null =>
  typeof value === "string" && value.trim() ? value.trim() : null;

/** A whole-number figure in the viewer's locale («1 240»). */
const whole = (value: number): string => formatNumber(Math.round(value));

/**
 * Words the measured figures behind a dropped order as the REAL cause
 * («Окно 08:05–09:00 недостижимо: ближайшее прибытие 10:02»), or `null` when the
 * reason carries no detail or `detail` has no shape this bundle recognizes.
 *
 * Keyed on the REASON first and the shape second, against `solver.py`
 * `_diagnose_drop` (BUG-675/677) — the shapes are listed on
 * `UnassignedReasonDetail` in `@/types/routes`. A partial shape renders nothing rather than a
 * half-filled sentence. `undetermined` has no detail by design: its heading
 * already says the cause is unknown.
 *
 * NOTE FOR REVIEWERS: the keys are static literals, but WHICH one renders depends
 * on runtime data — only the rendered-text tests in `buildResultToast.test.tsx`
 * prove each branch reaches a real sentence.
 */
export function describeUnassignedDetail(
  reason: string,
  detail: Record<string, unknown> | null | undefined,
  t: TFunction,
  options: DetailOptions = {},
): string | null {
  if (!detail) return null;
  const text = describeMeasuredDetail(reason, detail, t, options);
  // BUG-691: the routing service was down, so the figures are a straight-line
  // estimate — say so instead of presenting them as measured. Only MEASURED
  // reasons carry figures; a pick refused before planning or an order changed
  // during the build states a fact, which no estimate can make approximate.
  const measured =
    !isIneligiblePickReason(reason) &&
    reason !== "order_changed_during_build" &&
    reason !== "vehicle_changed_during_build";
  return text !== null && measured && detail.estimated === true
    ? t("routes.buildAlert.details.estimated", { text })
    : text;
}

/** Context for {@link describeUnassignedDetail}. */
export interface DetailOptions {
  /** See {@link UnassignedBreakdownOptions.routeDate}. */
  routeDate?: string | null;
}

/** The ISO day after `iso` (`YYYY-MM-DD`), or `null` for a malformed value. */
function nextIsoDay(iso: string | null | undefined): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso?.trim() ?? "");
  if (!match) return null;
  const next = new Date(
    Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]) + 1),
  );
  return next.toISOString().slice(0, 10);
}

/**
 * BUG-694: the two fragments a next-night window is worded with — WHICH night
 * («ночь на 23 окт.», or «ночь после даты маршрута» when the date is unknown)
 * and the van it was judged against («ночная машина «Ночь-1»»).
 */
function nightParams(
  detail: Record<string, unknown>,
  t: TFunction,
  options: DetailOptions,
): { night: string; vehicle: string } {
  const nightDate = nextIsoDay(options.routeDate);
  const name = nonEmptyString(detail.vehicle_name);
  return {
    night: nightDate
      ? t("routes.buildAlert.details.nightOf", {
          date: formatDayMonthSmartYear(nightDate),
        })
      : t("routes.buildAlert.details.nightAfterRouteDate"),
    vehicle: name
      ? t("routes.buildAlert.details.nightVehicle", { name })
      : t("routes.buildAlert.details.nightVehicleUnnamed"),
  };
}

/** {@link describeUnassignedDetail} without the `estimated` label. */
function describeMeasuredDetail(
  reason: string,
  detail: Record<string, unknown>,
  t: TFunction,
  options: DetailOptions,
): string | null {
  const nextNight = detail.next_night === true;

  // BUG-686: picks skipped before planning — worded where the refusal is too.
  if (isIneligiblePickReason(reason)) {
    return describeIneligiblePickDetail(reason, detail, t);
  }

  switch (reason) {
    case "time_window": {
      const arrival = nonEmptyString(detail.earliest_arrival);
      const start = nonEmptyString(detail.window_start);
      const end = nonEmptyString(detail.window_end);
      if (!arrival || !end) return null;
      if (nextNight) {
        // BUG-694: the window is the night AFTER the route date, served by a night van.
        const night = nightParams(detail, t, options);
        return start
          ? t("routes.buildAlert.details.timeWindowNight", {
              start,
              end,
              arrival,
              ...night,
            })
          : t("routes.buildAlert.details.timeWindowEndNight", {
              end,
              arrival,
              ...night,
            });
      }
      return start
        ? t("routes.buildAlert.details.timeWindow", { start, end, arrival })
        : t("routes.buildAlert.details.timeWindowEnd", { end, arrival });
    }
    case "shift": {
      const returnAt = nonEmptyString(detail.return_at);
      const shiftEnd = nonEmptyString(detail.shift_end);
      if (returnAt && shiftEnd) {
        return nextNight
          ? t("routes.buildAlert.details.shiftReturnNight", {
              shiftEnd,
              returnAt,
              ...nightParams(detail, t, options),
            })
          : t("routes.buildAlert.details.shiftReturn", {
              shiftEnd,
              returnAt,
            });
      }
      const needed = finiteNumber(detail.needed_min);
      const longest = finiteNumber(detail.longest_shift_min);
      if (needed !== null && longest !== null) {
        return nextNight
          ? t("routes.buildAlert.details.shiftLengthNight", {
              needed: whole(needed),
              longest: whole(longest),
              ...nightParams(detail, t, options),
            })
          : t("routes.buildAlert.details.shiftLength", {
              needed: whole(needed),
              longest: whole(longest),
            });
      }
      return null;
    }
    // BUG-696: every able van has already finished today's shift.
    case "shift_over": {
      const shiftEnd = nonEmptyString(detail.shift_end);
      const now = nonEmptyString(detail.now);
      if (!shiftEnd || !now) return null;
      return t("routes.buildAlert.details.shiftOver", { shiftEnd, now });
    }
    case "max_distance": {
      const needed = finiteNumber(detail.needed_km);
      const limit = finiteNumber(detail.limit_km);
      if (needed === null || limit === null) return null;
      return t("routes.buildAlert.details.distance", {
        needed: whole(needed),
        limit: whole(limit),
      });
    }
    case "max_duration": {
      const needed = finiteNumber(detail.needed_min);
      const limit = finiteNumber(detail.limit_min);
      if (needed === null || limit === null) return null;
      return t("routes.buildAlert.details.duration", {
        needed: whole(needed),
        limit: whole(limit),
      });
    }
    case "max_orders": {
      const limit = finiteNumber(detail.limit);
      if (limit === null) return null;
      return t("routes.buildAlert.details.maxOrders", { limit: whole(limit) });
    }
    case "capacity": {
      // Decimal fields may arrive as strings; a zero/absent dimension is simply
      // not mentioned (the tenant may plan by one dimension only).
      const weight = finiteNumber(detail.weight_kg);
      const volume = finiteNumber(detail.volume_m3);
      // BUG-718: an order that fits NO candidate van carries the largest van's
      // capacity per enforced dimension (`null` when the dimension is not
      // planned by or no van records it). Name only the dimension(s) the order
      // actually exceeds; stored results without max_* keep the wording below.
      const maxPayload = finiteNumber(detail.max_payload_kg);
      const maxVolume = finiteNumber(detail.max_volume_m3);
      const over: string[] = [];
      if (weight !== null && maxPayload !== null && weight > maxPayload) {
        over.push(
          t("routes.buildAlert.details.capacityWeightOver", {
            weight: `${formatNumber(weight)} ${t("units.kg")}`,
            max: `${formatNumber(maxPayload)} ${t("units.kg")}`,
          }),
        );
      }
      if (volume !== null && maxVolume !== null && volume > maxVolume) {
        over.push(
          t("routes.buildAlert.details.capacityVolumeOver", {
            volume: formatVolume(volume),
            max: formatVolume(maxVolume),
          }),
        );
      }
      if (over.length > 0) {
        return t("routes.buildAlert.details.capacityLargest", {
          parts: over.join("; "),
        });
      }
      const parts: string[] = [];
      if (weight !== null && weight > 0) {
        parts.push(`${formatNumber(weight)} ${t("units.kg")}`);
      }
      if (volume !== null && volume > 0) parts.push(formatVolume(volume));
      if (parts.length === 0) return null;
      return t("routes.buildAlert.details.capacity", {
        cargo: parts.join(" / "),
      });
    }
    case "no_room": {
      const able = finiteNumber(detail.vehicles_able);
      if (able === null) return null;
      return t("routes.buildAlert.details.noRoom", {
        count: Math.round(able),
        vehicles: whole(able),
      });
    }
    case "routes_quota": {
      const limit = finiteNumber(detail.limit);
      const used = finiteNumber(detail.used);
      if (limit !== null && used !== null) {
        return t("routes.buildAlert.details.routesQuota", {
          limit: whole(limit),
          used: whole(used),
        });
      }
      // The solve's own vehicle cap (no tariff figures attached by the caller).
      const allowed = finiteNumber(detail.routes_allowed);
      if (allowed !== null) {
        return t("routes.buildAlert.details.routesAllowed", {
          allowed: whole(allowed),
        });
      }
      return null;
    }
    // BUG-679: strict windows screened out before the solve.
    case "window_invalid": {
      const start = nonEmptyString(detail.window_start);
      const end = nonEmptyString(detail.window_end);
      if (!start || !end) return null;
      return t("routes.buildAlert.details.windowInvalid", { start, end });
    }
    case "window_after_shift": {
      const start = nonEmptyString(detail.window_start);
      const end = nonEmptyString(detail.window_end);
      const shiftEnd = nonEmptyString(detail.latest_shift_end);
      if (!start || !end || !shiftEnd) return null;
      return t("routes.buildAlert.details.windowAfterShift", {
        start,
        end,
        shiftEnd,
      });
    }
    // BUG-680: the placement pass's safety cap ran out with idle vans left.
    case "solver_time_limit": {
      const idle = finiteNumber(detail.idle_vans);
      if (idle === null) return null;
      return t("routes.buildAlert.details.solverTimeLimit", {
        count: Math.round(idle),
        vehicles: whole(idle),
      });
    }
    // BUG-713: an idle van could serve the order, but the planner failed to place it.
    case "planner_failed": {
      const idle = finiteNumber(detail.idle_vans);
      if (idle === null) return null;
      return t("routes.buildAlert.details.plannerFailed", {
        count: Math.round(idle),
        vehicles: whole(idle),
      });
    }
    // BUG-685: the day is full — with this order the van would be back after its
    // day ends (its shift end, or 24:00 with no shift). Times are clock times on
    // the route's date; a flag marks one that falls on the next day.
    case "day_full": {
      const returnAt = nonEmptyString(detail.return_at);
      const dayEnd = nonEmptyString(detail.day_end);
      if (!returnAt || !dayEnd) return null;
      const nextDay = (time: string, flag: unknown) =>
        flag === true
          ? t("routes.buildAlert.details.nextDayTime", { time })
          : time;
      const params = {
        returnAt: nextDay(returnAt, detail.return_next_day),
        dayEnd: nextDay(dayEnd, detail.day_end_next_day),
      };
      // A day ending exactly at midnight is no shift at all — «день», not «смена».
      return dayEnd === "24:00" && detail.day_end_next_day !== true
        ? t("routes.buildAlert.details.dayFull", params)
        : t("routes.buildAlert.details.dayFullShift", params);
    }
    // BUG-687…693: the order or its planned van changed while the build ran.
    case "order_changed_during_build":
      return describeOrderChangedDetail(detail, t);
    case "vehicle_changed_during_build":
      return describeVehicleChangedDetail(detail, t);
    default:
      return null;
  }
}

/**
 * BUG-686: the id of the route an `already_routed` order already sits on, so a
 * surface can link to it — or `null` for any other reason / a detail without one.
 */
export function alreadyRoutedRouteId(
  order: Pick<UnassignedNotificationOrder, "reason" | "detail">,
): number | null {
  if (order.reason !== "already_routed") return null;
  return readPickRoute(order.detail)?.id ?? null;
}

/** How many DIFFERENT details one address line lists before «…». */
const MAX_DETAILS_PER_LINE = 2;

/**
 * Collapses each reason's orders by ADDRESS with a count, keeping the first
 * `maxLines` addresses (most orders first, payload order on ties).
 *
 * BUG-676: a 200-order build listed 27 orders as one comma-joined paragraph in
 * which the same shop appeared three times. Several orders at one point are one
 * problem for the dispatcher — «Торговый объект "Копеечка", г. Толочин ×5».
 */
export function summarizeUnassignedByAddress(
  breakdown: UnassignedBreakdown,
  // Kept for the call sites: the details are now read off the breakdown's own
  // `orderDetails`, worded once with its options (e.g. the route date, BUG-694).
  _t: TFunction,
  maxLines: number = MAX_ADDRESSES_PER_REASON,
): UnassignedReasonSummary[] {
  return breakdown.groups.map((group) => {
    // Orders at one address can carry DIFFERENT figures (two windows at one shop:
    // 08:05–09:00 and 10:05–10:25), so each line keeps its distinct details —
    // never just the first one, which would misstate the other orders' cause.
    const byAddress = new Map<
      string,
      {
        label: string;
        count: number;
        details: string[];
        fromPermanent: boolean;
      }
    >();
    group.orders.forEach((order, index) => {
      const label = order.order_address?.trim() || unassignedOrderLabel(order);
      const key = addressKey(label) || label;
      // The group's own worded details (same options, e.g. the route date).
      const detail = group.orderDetails[index] ?? null;
      const existing = byAddress.get(key);
      const fromPermanent = isDeletedPermanentCopy(order);
      if (existing) {
        existing.count += 1;
        existing.fromPermanent ||= fromPermanent;
        if (detail && !existing.details.includes(detail)) {
          existing.details.push(detail);
        }
      } else {
        byAddress.set(key, {
          label,
          count: 1,
          details: detail ? [detail] : [],
          fromPermanent,
        });
      }
    });

    // Array.prototype.sort is stable, so equal counts keep payload order.
    const lines: UnassignedAddressLine[] = [...byAddress.values()]
      .sort((a, b) => b.count - a.count)
      .slice(0, Math.max(0, maxLines))
      .map(({ label, count, details, fromPermanent }) => ({
        label,
        count,
        fromPermanent,
        detail:
          details.length === 0
            ? null
            : details.length > MAX_DETAILS_PER_LINE
              ? `${details.slice(0, MAX_DETAILS_PER_LINE).join("; ")}; …`
              : details.join("; "),
      }));
    const shown = lines.reduce((sum, line) => sum + line.count, 0);

    return {
      reason: group.reason,
      label: group.label,
      lines,
      total: group.total,
      hiddenCount: Math.max(0, group.total - shown),
    };
  });
}
